import React, { useCallback, useEffect, useState } from 'react';
import { useForm as useHookForm, FormProvider } from 'react-hook-form';
import { Footer, TableTabbed } from 'components';
import { BenchmarkForm } from 'components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkForm';
import { useTable, useTimeWindow } from './hooks';
import { AbsoluteReturnTimeWindow } from './AbsoluteReturnTimeWindow';
import { useYupValidationResolver } from '../../helpers/form';
import {
  BenchmarkScreen,
  useBenchmarkDetails,
  useTWNavigation,
  useTWScreenParams,
  useTimeWindows,
} from 'hooks';
import { useSubmit } from 'hooks/useSubmit';
import { getBody } from './AbsoluteReturnHelpers';
import { showSuccess } from 'helpers/showSuccess';
import { DevTool } from '@hookform/devtools';
import {
  AbsoluteReturn,
  AbsoluteReturnTimeWindow as AbsoluteReturnTimeWindowType,
} from 'types/AbsoluteReturn';
import { BenchmarkMetadata } from 'components/BenchmarkMetadata';
import { SmallDisplay } from './components/SmallDisplay';
import { DefaultDisplay } from './components/DefaultDisplay';
import { getToDates } from 'helpers/timeWindow/getToDates';
import {
  absoluteReturnTimeWindowShape,
  absoluteReturnValidationSchema,
} from './AbsoluteReturnValidationScheme';
import { BenchmarkTypes } from 'types/Benchmark';
import { BenchmarkFormTitle } from 'components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkFormStyles';
import {
  DropdownSpace,
  TimeWindows,
  TimeWindowsEditTitle,
} from 'components/TimeWindow';
import { useProblemsHandler } from 'modules/BenchmarkForm/hooks/useProblemsHandler';
import { LoadingWrapper } from 'components/LoadingWrapper/LoadingWrapper';

type Props = {};

export const BenchmarkAbsoluteReturn: React.FC<Props> = () => {
  const [isFormLoadComplete, setIsFormLoadComplete] = useState(false);
  const resolver = useYupValidationResolver(absoluteReturnValidationSchema());

  const methods = useHookForm<any>({
    resolver,
    defaultValues: {
      baseCurrencyCode: 'USD',
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
    reset,
    setError,
  } = methods;

  const { timeWindows, timeWindowsFilled } =
    useTimeWindows<AbsoluteReturnTimeWindowType>({
      setValue,
      control,
      timeWindowSchema: absoluteReturnTimeWindowShape,
    });

  const {
    benchmark,
    refetch,
    isLoading: isLoadingBenchmark,
  } = useBenchmarkDetails<AbsoluteReturn>();

  const { isTimeWindowsScreen, addTimeWindow, benchmarkScreen } =
    useTWScreenParams(benchmark?.id);

  const handleTWNavigation = useTWNavigation(benchmarkScreen);

  const tableProps = useTable(timeWindows);

  const {
    onSubmit,
    isSuccess,
    isLoading: isSubmitting,
    errors: problemDetails,
  } = useSubmit({
    originalValues: benchmark,
    getBody,
    originalTimeWindows: timeWindows,
  });

  useProblemsHandler(setError, problemDetails);

  useEffect(() => {
    if (isSuccess) {
      if (benchmark?.id) {
        showSuccess('Benchmark was successfully saved');
        refetch();
        handleTWNavigation();
        window.scrollTo(0, 0);
      }
    }
  }, [isSuccess, benchmark?.id]);

  const resetData = useCallback(() => {
    reset();
    if (benchmark) {
      let values: any = {
        ...benchmark,
        timeWindows: getToDates(
          benchmark?.timeWindows?.map((item: any) => {
            return {
              fromDate: new Date(item.fromDate),
              basisPointAdjustment: item.basisPointAdjustment,
              returnComponent:
                item.returnComponentBenchmarkId ||
                item.returnComponentResourceId,
              noReturnComponent:
                item.returnComponentBenchmarkId ||
                  item.returnComponentResourceId
                  ? false
                  : true,
              name: item.returnComponentName
            };
          })
        ),
      };

      reset(values);
    }
  }, [benchmark, reset]);

  useEffect(() => {
    resetData();
  }, [resetData]);

  const tableTabbedProps = {
    ...tableProps,
    useTimeWindow,
    handleSubmit,
    onSubmit,
    isLoading: isSubmitting || isLoadingBenchmark,
    benchmarkName: benchmark?.name,
    benchmarkId: benchmark?.id,
  };

  const renderContent = () => {
    if (isLoadingBenchmark) return null;

    if (isTimeWindowsScreen || addTimeWindow) {
      return (
        <TimeWindows
          benchmarkType={BenchmarkTypes.AbsoluteReturn}
          timeWindowsFilled={timeWindowsFilled}
          benchmarkId={benchmark?.id}
          TimeWindowComponent={AbsoluteReturnTimeWindow}
          defaultValues={{
            fromDate: new Date(),
            basisPointAdjustment: '',
            returnComponent: '',
            noReturnComponent: false,
          }}
        />
      );
    }

    return <TableTabbed {...tableTabbedProps} />;
  };

  return (
    <LoadingWrapper isLoading={isLoadingBenchmark && isFormLoadComplete}>
      <TimeWindowsEditTitle isTimeWindowsScreen={isTimeWindowsScreen} />
      {benchmark?.id && !isSubmitting ? (
        <BenchmarkMetadata
          benchmark={benchmark}
          onConfirm={refetch}
          isTimeWindowsScreen={isTimeWindowsScreen}
        >
          <SmallDisplay benchmark={benchmark} />
          <DefaultDisplay benchmark={benchmark} />
        </BenchmarkMetadata>
      ) : null}
      <FormProvider {...methods}>
        {benchmark === undefined ? (
          <div style={{ padding: 20 }}>
            <BenchmarkFormTitle>
              Add New Absolute Return Benchmark
            </BenchmarkFormTitle>
            <BenchmarkForm
              benchmarkType={BenchmarkTypes.AbsoluteReturn}
              benchmark={undefined}
              onLoadComplete={() => setIsFormLoadComplete(true)}
            />
          </div>
        ) : null}
        {renderContent()}
        <Footer
          showReturnHome={benchmarkScreen === BenchmarkScreen.editBenchmark}
          onSave={handleSubmit((value: any) => {
            onSubmit({
              ...value,
              onSaveSuccess: isSuccess,
            });
          })}
          onDiscardChanges={() => {
            resetData();
            handleTWNavigation();
            window.scrollTo(0, 0);
          }}
          isSaveDisabled={!isDirty || errors.timeWindows}
          isLoading={tableTabbedProps.isLoading}
          showEditButtons={Boolean(isTimeWindowsScreen || !benchmark?.id)}
          discardText='Cancel'
          isDirty={isDirty}
        />
        <DevTool control={control} placement='top-left' />
      </FormProvider>
      <DropdownSpace />
    </LoadingWrapper>
  );
};
